import { dbAdapter } from './databaseAdapter';

export const inventoryService = {
  // INVENTORY ITEMS - LENGKAPI SEMUA METHOD

  // GET ALL
  getAll: async () => {
    try {
      return await dbAdapter.get('inventory');
    } catch (error) {
      console.error('Error getting inventory:', error);
      return [];
    }
  },

  // GET BY ID
  getById: async (id) => {
    try {
      return await dbAdapter.getById('inventory', id);
    } catch (error) {
      console.error('Error getting inventory item:', error);
      return null;
    }
  },

  // CREATE
  create: async (data) => {
    try {
      const newItem = {
        id: `INV-${Date.now()}`,
        ...data,
        photo: data.photo || '',
        status: data.status || 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return await dbAdapter.create('inventory', newItem);
    } catch (error) {
      console.error('Error creating inventory item:', error);
      throw new Error(error.message || 'Gagal membuat item inventory');
    }
  },

  // UPDATE
  update: async (id, data) => {
    try {
      const updateData = {
        ...data,
        photo: data.photo || '',
        updatedAt: new Date().toISOString()
      };
      return await dbAdapter.update('inventory', id, updateData);
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw new Error(error.message || 'Gagal mengupdate item inventory');
    }
  },

  // DELETE
  delete: async (id) => {
    try {
      // Check if item is used in any work order
      const workOrders = await dbAdapter.get('work_orders');
      const isUsed = workOrders.some(wo => 
        wo.materials && wo.materials.some(m => m.id === id) // ← FIX: id bukan inventoryId
      );
      if (isUsed) {
        throw new Error('Item is used in work orders. Cannot delete.');
      }
      return await dbAdapter.delete('inventory', id);
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw new Error(error.message || 'Gagal menghapus item inventory');
    }
  },

  // ============================================
  // CATEGORIES
  // ============================================
  getCategories: async () => {
    try {
      return await dbAdapter.get('inventory_categories');
    } catch (error) {
      console.error('Error getting categories:', error);
      return [];
    }
  },

  getCategoryById: async (id) => {
    try {
      return await dbAdapter.getById('inventory_categories', id);
    } catch (error) {
      console.error('Error getting category:', error);
      return null;
    }
  },

  createCategory: async (data) => {
    try {
      const newCategory = {
        id: `CAT-${Date.now()}`,
        ...data,
        createdAt: new Date().toISOString()
      };
      return await dbAdapter.create('inventory_categories', newCategory);
    } catch (error) {
      console.error('Error creating category:', error);
      throw new Error(error.message || 'Gagal membuat kategori');
    }
  },

  updateCategory: async (id, data) => {
    try {
      return await dbAdapter.update('inventory_categories', id, data);
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error(error.message || 'Gagal mengupdate kategori');
    }
  },

  deleteCategory: async (id) => {
    try {
      const items = await dbAdapter.get('inventory');
      const hasItems = items.some(item => item.categoryId === id);
      if (hasItems) {
        throw new Error('Category has items. Cannot delete.');
      }
      return await dbAdapter.delete('inventory_categories', id);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error(error.message || 'Gagal menghapus kategori');
    }
  },

  // ============================================
  // SUPPLIERS
  // ============================================
  getSuppliers: async () => {
    try {
      return await dbAdapter.get('suppliers');
    } catch (error) {
      console.error('Error getting suppliers:', error);
      return [];
    }
  },

  getSupplierById: async (id) => {
    try {
      return await dbAdapter.getById('suppliers', id);
    } catch (error) {
      console.error('Error getting supplier:', error);
      return null;
    }
  },

  createSupplier: async (data) => {
    try {
      const newSupplier = {
        id: `SUP-${Date.now()}`,
        ...data,
        createdAt: new Date().toISOString()
      };
      return await dbAdapter.create('suppliers', newSupplier);
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw new Error(error.message || 'Gagal membuat supplier');
    }
  },

  updateSupplier: async (id, data) => {
    try {
      return await dbAdapter.update('suppliers', id, data);
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw new Error(error.message || 'Gagal mengupdate supplier');
    }
  },

  deleteSupplier: async (id) => {
    try {
      const items = await dbAdapter.get('inventory');
      const hasItems = items.some(item => item.supplierId === id);
      if (hasItems) {
        throw new Error('Supplier has items. Cannot delete.');
      }
      return await dbAdapter.delete('suppliers', id);
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw new Error(error.message || 'Gagal menghapus supplier');
    }
  },

  // ============================================
  // DASHBOARD STATS
  // ============================================
  getDashboardStats: async () => {
    try {
      const items = await dbAdapter.get('inventory');
      const totalItems = items.length;
      const lowStock = items.filter(item => (item.stock || 0) <= (item.minStock || 0)).length;
      const criticalStock = items.filter(item => (item.stock || 0) <= (item.minStock || 0) * 0.5).length;
      const totalValue = items.reduce((sum, item) => sum + ((item.stock || 0) * (item.buyPrice || 0)), 0);
      
      return {
        totalItems,
        lowStock,
        criticalStock,
        totalValue
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      return {
        totalItems: 0,
        lowStock: 0,
        criticalStock: 0,
        totalValue: 0
      };
    }
  },

  // ============================================
  // STOCK OPERATIONS
  // ============================================
  addStock: async (id, quantity, note = '') => {
    try {
      const item = await dbAdapter.getById('inventory', id);
      if (!item) return null;
      
      const newStock = (item.stock || 0) + quantity;
      return await dbAdapter.update('inventory', id, { 
        stock: newStock,
        lastStockUpdate: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error adding stock:', error);
      throw new Error(error.message || 'Gagal menambah stock');
    }
  },

  removeStock: async (id, quantity, note = '') => {
    try {
      const item = await dbAdapter.getById('inventory', id);
      if (!item) return null;
      
      const currentStock = item.stock || 0;
      if (currentStock < quantity) {
        throw new Error(`Insufficient stock. Available: ${currentStock}`);
      }
      
      const newStock = currentStock - quantity;
      return await dbAdapter.update('inventory', id, { 
        stock: newStock,
        lastStockUpdate: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error removing stock:', error);
      throw new Error(error.message || 'Gagal mengurangi stock');
    }
  },

  // ============================================
  // FILTERS
  // ============================================
  getByCategory: async (categoryId) => {
    try {
      const items = await dbAdapter.get('inventory');
      return items.filter(item => item.categoryId === categoryId);
    } catch (error) {
      console.error('Error getting inventory by category:', error);
      return [];
    }
  },

  getBySupplier: async (supplierId) => {
    try {
      const items = await dbAdapter.get('inventory');
      return items.filter(item => item.supplierId === supplierId);
    } catch (error) {
      console.error('Error getting inventory by supplier:', error);
      return [];
    }
  },

  getLowStock: async () => {
    try {
      const items = await dbAdapter.get('inventory');
      return items.filter(item => (item.stock || 0) <= (item.minStock || 0));
    } catch (error) {
      console.error('Error getting low stock items:', error);
      return [];
    }
  },

  getCriticalStock: async () => {
    try {
      const items = await dbAdapter.get('inventory');
      return items.filter(item => (item.stock || 0) <= (item.minStock || 0) * 0.5);
    } catch (error) {
      console.error('Error getting critical stock items:', error);
      return [];
    }
  }
};